import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Camera, ChevronRight } from 'lucide-react';
import AdminBottomNav from '../../components/AdminBottomNav';
import { hostCardStyle } from '../../components/HostSettingsCard';
import { useProfilePhotoUpload } from '../../hooks/useProfilePhotoUpload';
import { resolveAvatarUrl } from '../../utils/resolveAvatarUrl';
import StaffVenueHeader from '../../components/staff/StaffVenueHeader';
import { RouteTransitionLogo } from '../../components/RouteTransitionLogo';
import ProfileThemeFlash from '../../components/ProfileThemeFlash';
import ProfilePalettePicker from '../../components/ProfilePalettePicker';
import LogoutConfirmModal from '../../components/LogoutConfirmModal';

export default function AdminProfilePage({ onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { fileInputRef, uploading, openPicker, handleFileChange, user } = useProfilePhotoUpload();
  const [confirmLogout, setConfirmLogout] = useState(false);

  const requestLogout = () => setConfirmLogout(true);
  const handleLogout = () => {
    setConfirmLogout(false);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    if (onLogout) onLogout();
    window.location.href = '/login';
  };

  const menuItems = [
    { label: 'Events', action: () => navigate('/staff-admin/events') },
    { label: 'Venue Settings', action: () => navigate('/staff-admin/settings') },
    { label: 'Account Settings', action: () => navigate('/staff-admin/account-settings') },
    { label: 'Demo QR scan', action: () => navigate('/demo-scan') },
    { label: 'Allure Support', action: () => navigate('/support') },
    { label: 'Switch to..', action: () => navigate('/switch-role', { state: { fromPath: location.pathname } }) },
  ];

  return (
    <div className="app-shell" data-testid="admin-profile-page" style={{ minHeight: '100dvh', backgroundColor: 'var(--app-bg)', display: 'flex', flexDirection: 'column', alignItems: 'center', paddingBottom: 120 }}>
      <ProfileThemeFlash />
      <StaffVenueHeader roleLabel="Admin staff" testIdPrefix="admin-profile" />
      <div style={{ marginTop: 16 }}>
        <RouteTransitionLogo width={132} height={118} dataTestId="admin-profile-shared-logo" />
      </div>

      {/* Profile card */}
      <div className="profile-name-card" style={{ ...hostCardStyle, width: 'calc(100% - 48px)', maxWidth: 340, marginTop: 12, background: 'var(--profile-card-gradient)', borderRadius: 20, border: 'none', boxShadow: 'none', padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 16 }}>
        <div style={{ position: 'relative' }}>
          <div
            onClick={openPicker}
            data-testid="admin-profile-photo-target"
            style={{
              width: 72, height: 72, borderRadius: '50%', backgroundColor: '#E0D8C8',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: '2px solid #fff', boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
              overflow: 'hidden', cursor: uploading ? 'wait' : 'pointer',
              opacity: uploading ? 0.7 : 1, transition: 'opacity 200ms ease',
            }}
          >
            {resolveAvatarUrl(user?.photo_url) ? (
              <img src={resolveAvatarUrl(user.photo_url)} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <span style={{ fontFamily: "'Inria Serif', serif", fontSize: 24, fontWeight: 700, color: 'var(--text-2)' }}>{(user.staff_name || user.name || 'A').charAt(0)}</span>
            )}
          </div>
          <div
            onClick={openPicker}
            role="button"
            aria-label="Change profile photo"
            data-testid="admin-profile-photo-edit-btn"
            style={{ position: 'absolute', bottom: -2, left: -2, width: 26, height: 26, borderRadius: '50%', backgroundColor: 'var(--surface)', border: '1px solid #E53935', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: uploading ? 'wait' : 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}
          >
            <Camera size={13} color="#E53935" />
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            style={{ display: 'none' }}
            data-testid="admin-profile-photo-input"
          />
        </div>
        <div>
          <p style={{ fontFamily: "'Inria Serif', serif", fontSize: 22, fontWeight: 700, color: 'var(--text)', margin: 0 }}>{user.staff_name || user.name || 'Admin'}</p>
          <p style={{ fontFamily: "'Inria Serif', serif", fontSize: 16, fontWeight: 400, color: 'var(--text-2)', margin: 0 }}>Venue admin</p>
        </div>
      </div>

      {/* Palette preview strip — tap a dot to retint the whole app live. */}
      <div style={{ width: 'calc(100% - 48px)', maxWidth: 340 }}>
        <ProfilePalettePicker testIdPrefix="admin-profile-palette" />
      </div>

      <div style={{ width: 'calc(100% - 48px)', maxWidth: 340, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
        {menuItems.map((item) => (
          <button key={item.label} onClick={item.action} data-testid={`admin-profile-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            style={{ ...hostCardStyle, width: '100%', background: 'var(--surface)', border: '1px solid var(--card-border)', cursor: 'pointer', padding: '14px 16px', fontFamily: "'Inria Serif', serif", fontSize: 17, color: 'var(--text)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', textAlign: 'left' }}>
            <span>{item.label}</span>
            <ChevronRight size={18} color="var(--text-2)" />
          </button>
        ))}
        <button onClick={handleLogout} data-testid="admin-profile-logout"
          style={{ ...hostCardStyle, width: '100%', background: 'var(--surface)', border: '1px solid var(--accent-purple)', cursor: 'pointer', padding: '14px 16px', marginTop: 2, fontFamily: "'Inria Serif', serif", fontSize: 18, fontWeight: 700, color: 'var(--accent-purple)' }}>
          Log Out
        </button>
      </div>
      <AdminBottomNav />
    </div>
  );
}
